import { RefObject, useCallback } from 'react';

/**
 * Manually manages cursor placement and text selection via pointer events.
 * This is necessary because we call preventDefault on pointerdown (to stop the
 * select menu from toggling on every click), which also disables native caret behavior.
 * When lockActiveState is true, native behavior is preserved and this is a no-op.
 */
const useHandleManualCursorAndSelection = ({
  lockActiveState,
  selectionAnchorRef,
}: {
  lockActiveState: boolean;
  selectionAnchorRef: RefObject<number | null>;
}) => {
  return useCallback(
    (e: React.PointerEvent<HTMLInputElement>) => {
      if (!lockActiveState) {
        e.stopPropagation();
        e.preventDefault();
      }

      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) {
        const input = e.currentTarget;

        // shift+click: extend selection from anchor (or caret) to clicked position
        if (e.shiftKey) {
          const anchor = selectionAnchorRef.current ?? input.selectionStart ?? 0;
          const clickOffset = pos.offset;
          const start = Math.min(anchor, clickOffset);
          const end = Math.max(anchor, clickOffset);
          input.setSelectionRange(start, end, clickOffset < anchor ? 'backward' : 'forward');
          return;
        }

        const startOffset = pos.offset;
        selectionAnchorRef.current = startOffset;
        input.setSelectionRange(startOffset, startOffset);

        // track pointer move to support drag-to-highlight
        // listeners are on document so dragging outside the input still works
        // cache the bounding rect to avoid forced reflows on every pointer move
        const inputRect = input.getBoundingClientRect();
        let lastOffset = startOffset;
        let rafId: number | null = null;

        const onPointerMove = (moveEvent: PointerEvent) => {
          // throttle with requestAnimationFrame to avoid jank on older devices
          if (rafId !== null) return;

          rafId = requestAnimationFrame(() => {
            rafId = null;

            const movePos = document.caretPositionFromPoint(moveEvent.clientX, moveEvent.clientY);
            const isWithinInput =
              moveEvent.clientX >= inputRect.left &&
              moveEvent.clientX <= inputRect.right &&
              moveEvent.clientY >= inputRect.top &&
              moveEvent.clientY <= inputRect.bottom;

            if (movePos && isWithinInput) {
              lastOffset = movePos.offset;
            }
            const start = Math.min(startOffset, lastOffset);
            const end = Math.max(startOffset, lastOffset);
            input.setSelectionRange(start, end, lastOffset < startOffset ? 'backward' : 'forward');
          });
        };
        const onPointerUp = () => {
          // cancel any pending RAF to avoid updates after cleanup
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          document.removeEventListener('pointermove', onPointerMove);
          document.removeEventListener('pointerup', onPointerUp);
        };
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
      }
    },
    [lockActiveState, selectionAnchorRef]
  );
};

export default useHandleManualCursorAndSelection;
